import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Card, CardContent, Button } from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Legend, Cell, Area, AreaChart } from "recharts";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import { getProductPerformanceReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";

const drawerFilters = [
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const ProductPerformanceReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [summary, setSummary] = useState({
        totalRevenue: 0,
        totalProducts: 0,
        topRevenueProduct: "-",
        fastestGrowingProduct: "-",
        mostCustomersProduct: "-",
    });

    const [products, setProducts] = useState([]);

    const buildApiFilters = (fv) => ({
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getProductPerformanceReportAction({
                data: buildApiFilters(fv),
            });
            setSummary(response?.data?.summary || {});
            setProducts(response?.data?.products || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchReport(resetValues);
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const hasAppliedFilters = false; // date range always pre-set, no extra indicator needed

    const revenueChartData = products.map((item) => ({
        name: item.product,
        revenue: item.revenue,
    }));

    const tenantChartData = products.map((item) => ({
        name: item.product,
        value: item.tenants,
    }));

    return (
        <>
            <Grid container spacing={3}>
                {/* Filters */}
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">
                                        Product Performance Report
                                    </h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* KPI Cards */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#eef4ff", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Revenue</Typography>
                            <Typography variant="h4">
                                {Number(summary.totalRevenue || 0).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#ecfeff", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Inventory2Icon sx={{ color: "#0891b2" }} />
                                <Typography variant="h6">Total Products</Typography>
                            </Box>
                            <Typography variant="h4">{summary.totalProducts || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#f5f3ff", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <EmojiEventsIcon sx={{ color: "#7c3aed" }} />
                                <Typography variant="h6">Top Revenue Product</Typography>
                            </Box>
                            <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                                {summary.topRevenueProduct || "-"}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#f0fdf4", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <GroupsIcon sx={{ color: "#16a34a" }} />
                                <Typography variant="h6">Most Customers</Typography>
                            </Box>
                            <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                                {summary.mostCustomersProduct || "-"}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Charts */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" mb={2}>
                                    Revenue By Product
                                </Typography>
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={revenueChartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="revenue">
                                            {revenueChartData.map((entry, index) => (
                                                <Cell
                                                    key={index}
                                                    fill={
                                                        entry.name === "QBot"
                                                            ? "#22419e"
                                                            : entry.name === "PayCentral"
                                                                ? "#16a34a"
                                                                : entry.name === "KYC"
                                                                    ? "#8b5cf6"
                                                                    : "#f97316"
                                                    }
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" mb={2}>
                                    Tenant Distribution
                                </Typography>
                                <ResponsiveContainer width="100%" height={350}>
                                    <PieChart>
                                        <Pie
                                            data={tenantChartData}
                                            dataKey="value"
                                            nameKey="name"
                                            outerRadius={120}
                                            label
                                        >
                                            {tenantChartData.map((entry, index) => (
                                                <Cell
                                                    key={index}
                                                    fill={
                                                        entry.name === "QBot"
                                                            ? "#22419e"
                                                            : entry.name === "PayCentral"
                                                                ? "#16a34a"
                                                                : entry.name === "KYC"
                                                                    ? "#8b5cf6"
                                                                    : "#f97316"
                                                    }
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="h6" mb={2}>
                                    Revenue Distribution
                                </Typography>
                                <Box height={350}>
                                    <ResponsiveContainer>
                                        <AreaChart data={revenueChartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Area
                                                type="monotone"
                                                dataKey="revenue"
                                                stroke="#22419e"
                                                fill="rgba(34,65,158,0.15)"
                                                strokeWidth={3}
                                                dot={{ fill: "#22419e", stroke: "#22419e", r: 5 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default ProductPerformanceReport;
